// src/components/Services/Services_ITSupport.jsx
import React from "react";
import { motion } from "framer-motion";
import itSupportGif from "../images/photos/support1.gif"; // ✅ correct path

const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1 },
  out: { opacity: 0 },
};

const pageTransition = {
  duration: 0.6,
};

const fadeUp = {
  hidden: { y: 20, opacity: 0 },
  visible: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.08 * i },
  }),
};

export default function Services_ITSupport() {
  const services = [
    "Android Zero Touch Enrollment",
    "Android Management",
    "Cloud Computing",
    "Cloud Management Service",
    "Cyber Security",
    "iOS Management",
    "Mobile Management",
    "IT Management",
    "IT Support",
    "Software Development",
    "Digital Marketing",
  ];

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{
        minHeight: "100vh",
        background: "#040d1fff",
        color: "#e5e9edff",
        padding: "20px",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* HERO SECTION */}
        <section className="hero">
          {/* LEFT - GIF */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            style={{ textAlign: "center" }}
          >
            <img
              src={itSupportGif}
              alt="IT Support demo"
              style={{
                width: "100%",
                maxWidth: 480,
                height: "auto",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              }}
              loading="lazy"
            />
          </motion.div>

          {/* RIGHT - TEXT */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
          >
            <h1
              style={{
                marginTop: 0,
                fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                color: "#6dd4e8ff",
              }}
            >
              IT Support
            </h1>
            <p
              style={{
                lineHeight: 1.6,
                fontSize: "clamp(0.95rem, 2vw, 1.1rem)",
                color: "#e2e8f0",
              }}
            >
              Our IT Support services ensure that your organization’s technology
              runs smoothly and securely. From troubleshooting and monitoring to
              cybersecurity and cloud management, we provide comprehensive
              support tailored to your business needs.
            </p>

            <h3
              style={{
                marginTop: 16,
                fontSize: "clamp(1.1rem, 2.5vw, 1.4rem)",
                color: "#53bdd2ff",
              }}
            >
              Key IT Support Services
            </h3>
            <ul
              style={{
                lineHeight: 1.7,
                fontSize: "clamp(0.9rem, 2vw, 1.05rem)",
                color: "#f1f5f9",
                paddingLeft: "1.2rem",
              }}
            >
              <li>
                <strong>Help Desk Support</strong> — fast resolution of user
                issues via phone, chat, or email.
              </li>
              <li>
                <strong>Remote & On-Site Support</strong> — efficient issue
                resolution from anywhere.
              </li>
              <li>
                <strong>Network & Infrastructure</strong> — monitoring, setup,
                and troubleshooting.
              </li>
              <li>
                <strong>Hardware & Software Support</strong> — installation,
                maintenance, and upgrades.
              </li>
              <li>
                <strong>Cybersecurity</strong> — protection against malware,
                phishing, and cyber threats.
              </li>
              <li>
                <strong>Backup & Disaster Recovery</strong> — reliable data
                protection and recovery.
              </li>
              <li>
                <strong>Cloud Support</strong> — managing and optimizing
                cloud-based systems.
              </li>
              <li>
                <strong>System Monitoring</strong> — proactive issue detection
                and prevention.
              </li>
            </ul>

            {/* Buttons */}
            <div
              style={{
                marginTop: 18,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <a
                href="mailto:support@zyntro.in"
                style={{
                  background: "#f69f48ff",
                  color: "white",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: "clamp(0.9rem, 2vw, 1rem)",
                }}
              >
                Email Support
              </a>
              <a
                href="tel:+919956353236"
                style={{
                  border: "1px solid #4e4e54ff",
                  color: "#010101ff",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                  background: "white",
                  fontSize: "clamp(0.9rem, 2vw, 1rem)",
                }}
              >
                Call +91-9956353236
              </a>
            </div>
          </motion.div>
        </section>

        {/* OTHER SERVICES */}
        <section style={{ marginTop: 50 }}>
          <h3
            style={{
              color: "#4fe1f4ff",
              fontSize: "clamp(1.2rem, 3vw, 1.6rem)",
              textAlign: "center",
            }}
          >
            Other Services
          </h3>
          <div className="services-grid">
            {services.map((s) => (
              <div key={s} className="service-card">
                <div className="icon">{s.charAt(0)}</div>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ✅ Responsive CSS */}
      <style>{`
        .hero {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          align-items: center;
        }
        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          margin-top: 20px;
        }
        .service-card {
          background: #1e293b;
          padding: 14px 12px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .service-card .icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: #1e7d95ff;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          flex-shrink: 0;
        }
        .service-card span {
          font-size: clamp(0.85rem, 2vw, 1rem);
        }

        /* ✅ Responsive */
        @media (max-width: 1024px) {
          .hero {
            grid-template-columns: 1fr;
            text-align: center;
          }
          .hero img {
            margin: 0 auto;
          }
          .hero ul {
            text-align: left;
          }
        }

        @media (max-width: 600px) {
          .services-grid {
            grid-template-columns: 1fr;
          }
          .service-card {
            justify-content: center;
            text-align: center;
          }
        }
      `}</style>
    </motion.div>
  );
}
